import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { Button, Fade, Icon, OptionGroup } from '@column/column-ui-kit';

import { useBreadcrumbTitle } from '~/app/Layout/PageBreadcrumbs';
import { PageActions } from '~/app/Layout/PageFrameContext';
import { ROUTE } from '~/app/routes';
import { CopyInput, Loading, RenderFields, RestrictedBanner } from '~/components';
import { useFormErrorHandler } from '~/hooks/useFormErrorHandler';
import { useNavigate } from '~/lib/navigation';
import { EntityRepository, EntityWithDetails } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Flex, Hr, Inner } from '~/styles';
import { ApiError } from '~/typings/API';
import { ApiErrCode } from '~/typings/enum';

import { BusinessEntity } from './_partials/BusinessEntity';
import { PersonEntity } from './_partials/PersonEntity';
import {
  BeneficialOwnerFormValues,
  BusinessEntityFormValues,
  EntityFormType,
  PersonEntityFormValues,
  applyBusinessDefaults,
  applyPersonDefaults,
  buildBusinessRequest,
  buildPersonRequest,
  createDefaultPersonFormValues,
  createInitialBusinessFormState,
  useEntityForm,
} from './_util/useEntityForm';

export { EntityFormType as EntityTypes };

interface Params {
  id: string;
  type: string;
}

export const PageEntitiesEdit: React.FC = () => {
  const { currentUser, currentPlatform, isAuthorized } = useSessionStore();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const navigate = useNavigate();
  const { handleSubmitErrors } = useFormErrorHandler();
  const { id, type: typeParam } = useParams<keyof Params>() as Params;
  const type = (
    typeParam === EntityFormType.Person || typeParam === EntityFormType.Business ? typeParam : EntityFormType.Person
  ) as EntityFormType;
  const [displayType, setDisplayType] = useState<EntityFormType>(type);
  const previousTypeRef = useRef<EntityFormType>(type);

  const form = useEntityForm(displayType);

  const {
    data: entityData,
    isLoading,
    error: entityError,
  } = useQuery<EntityWithDetails>({
    queryKey: [currentPlatform?.id, 'entity', id],
    queryFn: () => EntityRepository.get({ id }),
    enabled: !!id && !!currentUser,
    retry: (failureCount: number, error: unknown) => {
      const apiError = error as Partial<ApiError> | undefined;
      if (apiError?.code === ApiErrCode.ResourceNotFound.toString()) {
        return false;
      }
      return failureCount < 3;
    },
  });

  const entityName = id
    ? type === EntityFormType.Person
      ? [entityData?.personDetails?.firstName, entityData?.personDetails?.lastName].filter(Boolean).join(' ')
      : entityData?.businessDetails?.businessName
    : undefined;

  useBreadcrumbTitle(entityName);

  useEffect(() => {
    if (entityError) {
      navigate(ROUTE.ENTITIES);
      addDangerNotification({
        content: (entityError as Error).message ?? 'Failed to load entity',
      });
    }
  }, [entityError, navigate, addDangerNotification]);

  useEffect(() => {
    if (!entityData) {
      return;
    }

    if (type === EntityFormType.Person) {
      form.reset(
        applyPersonDefaults({
          ...entityData.personDetails,
          id: entityData.id,
        })
      );
    } else {
      form.reset(
        applyBusinessDefaults({
          ...entityData.businessDetails,
          id: entityData.id,
          beneficialOwners: (entityData.businessDetails?.beneficialOwners ?? []).map((owner) => ({
            ...owner,
          })) as BeneficialOwnerFormValues[],
        })
      );
    }
  }, [entityData, type, form]);

  useEffect(() => {
    if (type !== previousTypeRef.current) {
      previousTypeRef.current = type;
    } else if (displayType !== type && !id) {
      setDisplayType(type);
    }
  }, [type, id, displayType]);

  const handlePersonFormFadeOut = () => {
    if (!id && type === EntityFormType.Business && displayType === EntityFormType.Person) {
      form.reset(createInitialBusinessFormState());
      setDisplayType(EntityFormType.Business);
    }
  };

  const handleBusinessFormFadeOut = () => {
    if (!id && type === EntityFormType.Person && displayType === EntityFormType.Business) {
      form.reset(createDefaultPersonFormValues());
      setDisplayType(EntityFormType.Person);
    }
  };

  useEffect(() => {
    if (!id) {
      if (displayType === EntityFormType.Person) {
        form.reset(createDefaultPersonFormValues());
      } else {
        form.reset(createInitialBusinessFormState());
      }
    }
  }, [id, displayType, form]);

  const handleSubmit = form.handleSubmit(async () => {
    if (!currentUser) return;

    const values = form.getValues() as PersonEntityFormValues | BusinessEntityFormValues;

    try {
      if (id) {
        if (type === EntityFormType.Person) {
          const payload = buildPersonRequest(values as PersonEntityFormValues);
          const response = await EntityRepository.updatePerson(id, payload);
          addSuccessNotification({
            content: `${response.personDetails?.firstName} ${response.personDetails?.lastName} updated`,
          });
        } else {
          const payload = buildBusinessRequest(values as BusinessEntityFormValues);
          const response = await EntityRepository.updateBusiness(id, payload);
          addSuccessNotification({
            content: `${response.businessDetails?.businessName} updated`,
          });
        }
      } else if (type === EntityFormType.Person) {
        const payload = buildPersonRequest(values as PersonEntityFormValues);
        const response = await EntityRepository.createPerson(payload);
        navigate(ROUTE.ENTITIES);
        addSuccessNotification({
          content: `${response.personDetails?.firstName} ${response.personDetails?.lastName} added`,
        });
      } else {
        const payload = buildBusinessRequest(values as BusinessEntityFormValues);
        const response = await EntityRepository.createBusiness(payload);
        navigate(ROUTE.ENTITIES);
        addSuccessNotification({
          content: `${response.businessDetails?.businessName} added`,
        });
      }
    } catch (error: any) {
      addDangerNotification({ content: error?.message ?? 'Unable to save entity' });
    }
  }, handleSubmitErrors);

  return (
    <FormProvider {...form}>
      <PageActions>
        <Button
          onClick={handleSubmit}
          isDisabled={!isAuthorized({ permission: 'entities', permissionLevel: 'write' })}
          size="small"
          icon={!id && <Icon.Plus />}
        >
          {id ? 'Save Changes' : 'Create Entity'}
        </Button>
      </PageActions>
      <Loading isLoading={isLoading} fullHeight>
        {!isAuthorized({ permission: 'entities', permissionLevel: 'write' }) && (
          <Inner px="var(--page-margin)">
            <RestrictedBanner />
          </Inner>
        )}

        {RenderFields<Params>({
          sections: [
            {
              fields: [
                {
                  id: 'id',
                  label: 'ID',
                  hide: !id,
                  children: ({ value }) => <CopyInput value={(value as string) ?? ''} isMonospaced />,
                },
                {
                  id: 'type',
                  label: 'Entity Type',
                  children: () => (
                    <OptionGroup orientation={OptionGroup.Orientation.Horizontal}>
                      {[
                        { label: 'Person', value: EntityFormType.Person },
                        { label: 'Business', value: EntityFormType.Business },
                      ].map(({ label, value }) => (
                        <OptionGroup.Option
                          key={label}
                          isActive={type === value}
                          isDisabled={!!id}
                          onClick={() => !id && navigate(`${ROUTE.ENTITIES}/edit/${value}`)}
                        >
                          <OptionGroup.Option.Radio />
                          <OptionGroup.Option.Label>{label}</OptionGroup.Option.Label>
                        </OptionGroup.Option>
                      ))}
                    </OptionGroup>
                  ),
                },
              ],
            },
          ],
        })}
        <Hr />

        <Inner p={0} data-disabled={!isAuthorized({ permission: 'entities', permissionLevel: 'write' })}>
          <Fade
            show={displayType === EntityFormType.Person && (Boolean(id) || type === EntityFormType.Person)}
            onExited={handlePersonFormFadeOut}
          >
            {displayType === EntityFormType.Person && (
              <PersonEntity id={id} disableForm={!isAuthorized({ permission: 'entities', permissionLevel: 'write' })} />
            )}
          </Fade>
          <Fade
            show={displayType === EntityFormType.Business && (Boolean(id) || type === EntityFormType.Business)}
            onExited={handleBusinessFormFadeOut}
          >
            {displayType === EntityFormType.Business && (
              <BusinessEntity
                id={id}
                disableForm={!isAuthorized({ permission: 'entities', permissionLevel: 'write' })}
              />
            )}
          </Fade>
        </Inner>
        <Inner px="var(--page-margin)">
          <Flex justifyContent="space-between" alignItems="center">
            <Button onClick={() => navigate(ROUTE.ENTITIES)} variant="secondary" size="small">
              {id ? 'Back' : 'Cancel'}
            </Button>
            <Button
              onClick={handleSubmit}
              isDisabled={!isAuthorized({ permission: 'entities', permissionLevel: 'write' })}
              size="small"
              icon={!id && <Icon.Plus />}
            >
              {id ? 'Save Changes' : 'Create Entity'}
            </Button>
          </Flex>
        </Inner>
      </Loading>
    </FormProvider>
  );
};
